import { ArtistAvatar } from '@/components/artist-avatar';
import {
  Combobox,
  type ComboboxFetchParams,
  type ComboboxOption,
  type ComboboxPage,
} from '@/components/combobox';
import { queryArtists } from '@/lib/artists';

/** An artist option carrying everything callers might need to extract. */
export interface ArtistOption extends ComboboxOption {
  name: string;
}

/** Server-driven page loader for the artist combobox (shared, so it's stable). */
export async function fetchArtistPage({
  search,
  page,
}: ComboboxFetchParams): Promise<ComboboxPage<ArtistOption>> {
  const result = await queryArtists({ search, order: 'name', page, pageSize: 20 });
  return {
    items: result.items.map((artist) => ({
      id: artist.id,
      label: artist.name,
      name: artist.name,
      leading: (
        <ArtistAvatar name={artist.name} src={artist.avatarUrl} className="size-6 text-xs" />
      ),
    })),
    hasMore: page < result.totalPages,
  };
}

interface ArtistSelectProps {
  value: string;
  onChange: (option: ArtistOption) => void;
  placeholder?: string;
}

/** Searchable, auto-paginating artist picker (hands back the full option). */
export function ArtistSelect({
  value,
  onChange,
  placeholder = 'Search artists',
}: ArtistSelectProps) {
  return (
    <Combobox<ArtistOption>
      value={value}
      onChange={onChange}
      fetchPage={fetchArtistPage}
      cacheKey="artists"
      placeholder={placeholder}
      emptyLabel="No artists found."
    />
  );
}
